import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import qs from 'qs';
import moment from 'moment';

import _pathOr from 'lodash/fp/pathOr';
import _isEmpty from 'lodash/fp/isEmpty';
import _pluck from 'lodash/fp/pluck';
import _cloneDeep from 'lodash/fp/cloneDeep';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createTable from '../components/table';
import createFilters from '../../shared/filters';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';

const table = createTable({
	namespace: 'bmAccountsList',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

const filters = createFilters({
	namespace: 'bmAccountsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [{
		name: 'categories',
		title: 'Categories',
		placeholder: 'Add categories',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.BM_ACCOUNTS_CATEGORIES
		}
	}]
});

const calendar = createCalendar({
	namespace: 'bmAccountsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment('2021-08-01').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const FETCH_DATA_SUCCESS = 'bmAccountsList/fetch-data-success';
const REMOVE_SUCCESS = 'bmAccountsList/remove-data-success';
const REQUEST_ERROR = 'bmAccountsList/request-error';
const TOGGLE_PROGRESS_BAR = 'bmAccountsList/toggle-progress-bar';
const GOTO_PAGE_SUCCESS = 'bmAccountsList/goto-page-success';
const APPLY = 'bmAccountsList/apply';
const CLEAR = 'bmAccountsList/clear';
const SET_SEARCH_QUERY = 'bmAccountsList/set-search-query';

export const allowedPermissions = {
	'view': 'bm_accounts_view',
	'edit': 'bm_accounts_view'
};

const initialState = {
	data: null,
	showProgressBar: false,
	table: table.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState,
	search: '',
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = qs.parse(_pathOr({}, ['location', 'query'], props));

			dispatch(request(getState, externalQuery))
				.then(() => {
					if (externalQuery.entity)	{
						const filtersList = externalQuery.entity.filters || [];
						dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
					}
				});
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('accountsSearchForm')(getState());
			const search = searchForm && searchForm.search;
			dispatch({
				type: APPLY,
				payload: search
			});
			dispatch(request(getState));
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(getState, dispatch));
		},
		removeAccount: id => (dispatch, getState) => {
			const removingGood = getState().bmAccountsList.data.find(g => g.id === id);
			return confirm(`Are you sure you want to DELETE account ${removingGood.name} with ID ${removingGood.id}`) &&
				http({
					url: `${endpoints.BM_ACCOUNTS.SINGLE.replace('{id}', id)}`,
					method: 'delete',
					actions: [REMOVE_SUCCESS]
				})(dispatch)
					.then(() => actions.fetchData()(dispatch, getState));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('accountsSearchForm')(getState());
			const search = _pathOr('', ['search'])(searchForm);

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => dispatch => {
			dispatch(actions.toggleProgressBar());
			setTimeout(() => dispatch({ type: CLEAR }));
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			request(getState)(dispatch);
		}

	},
	table.actions,
	filters.actions,
	calendar.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data,
			showProgressBar: false,
			error: false
		}),
		[REMOVE_SUCCESS]: (state, action) => ({
			...state
		}),
		[APPLY]: (state, action) => ({
			...state,
			search: action.payload,
			showProgressBar: true
		}),
		[CLEAR]: () => initialState,
		[REQUEST_ERROR]: state => ({
			...state, showProgressBar: true
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	table.reducers,
	filters.reducers,
	calendar.reducers
);

export default handleActions(reducers, _cloneDeep(initialState));

export const selectors = {
	getData: state => state.bmAccountsList.data,
	getTable: table.selectors.getTable,
	getFilters: state => filters.selectors.getFilters(
		state => state.bmAccountsList.filters,
		state => state
	)(state),
	getSearchData: state => ({ search: state.bmAccountsList.search }),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.bmAccountsList.calendar,
		state => state
	)(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getIntl: state => intl(state),
	getProgressBar: state => state.bmAccountsList.showProgressBar,
	getError: state => state.bmAccountsList.error
};

function request(getState) {
	const {
		search, calendar,
		filters: { selectedFilters },
		table: { sortByColumn }
	} = getState().bmAccountsList;

	let filters = {};

	if (selectedFilters && !_isEmpty(selectedFilters)) {
		selectedFilters.forEach(filter => {
			const values = _pluck('value')(filter.value);
			if (values.length) {
				const selectedFilterValues = filters;
				selectedFilterValues[filter.name] = values;
			}
		});
	}

	const query = qs.stringify({search, filters,
		date_range: getDateRangeFromCalendar(calendar),
		sort: [sortByColumn.index, sortByColumn.direction]
	});

	return http({
		url: `${endpoints.BM_ACCOUNTS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, REQUEST_ERROR]
	});
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/bm-accounts/list/index.js